import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {type UserReportReasonEnum} from '@colonist/game-base-types'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints.ts'
import {UIComponentDropdownController, type DropdownContent} from '../../../shared/scripts/ui_components/UIComponentDropdownController.ts'
import {UIPopupReportPlayer} from '../../../shared/scripts/ui_popup/UIPopupReportPlayer.ts'
import {UIProfileFriendsTableController} from '../../../shared/scripts/profile/UIProfileFriendsTableController.ts'
import {UIUtilsFriends} from '../../../shared/scripts/ui_friends/UIUtilsFriends.ts'
import {ViewPopupLogin} from '../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin.ts'
import {ClientUserState} from '../../../shared/scripts/ClientUserState.ts'
import {type UIProfileLinkController} from '../../../shared/scripts/profile/UIProfileLinkController.ts'
import {doActionIfCanGiftMembership} from '../../../shared/scripts/store/StoreUtils.ts'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../shared/entity_datas/EntityDatas.ts'
import {CA} from '../../../shared/scripts/analytics/Analytics.ts'
import {LobbyImage} from '@colonist/types'
import {navigateToPage} from '../ui_utils/ClientUtils.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {getProfilePopupElementId} from './UIViewProfilePopup.ts'

export class UIProfileFriendsController {
    dropdownController: UIComponentDropdownController
    private profileDropdownContent: DropdownContent[] = []
    profileIsCurrentUser: boolean
    private userFriendsData: AjaxDataTypes.UserFriendsData
    private username: string

    private readonly profileLinkController: UIProfileLinkController

    private isFriendOrPending: boolean = false
    private readonly friendStateDropdownContent: Record<string, DropdownContent>

    private readonly toggleVisibilityComponent: HTMLDivElement | undefined
    private readonly isPopup: boolean

    constructor(username: string, profileLinkController: UIProfileLinkController, toggleVisibilityComponent?: HTMLDivElement, isPopup: boolean = false) {
        this.username = username
        this.profileLinkController = profileLinkController
        this.toggleVisibilityComponent = toggleVisibilityComponent
        this.isPopup = isPopup

        this.friendStateDropdownContent = {
            friend: {
                id: getProfilePopupElementId('profile_dropdown_removeFriend', this.isPopup),
                class: 'component-dropdown-red',
                text: {key: 'strings:profilePage.friends.removeFriend.title'},
                action: () => void this.showRemoveFriendPopup(),
            },
            notFriend: {
                id: getProfilePopupElementId('profile_dropdown_add_friend', this.isPopup),
                class: '',
                text: {key: 'strings:game.playerOptionsPopup.addFriend'},
                action: () => void this.addFriendDropdownAction(),
            },
            pending: {
                id: getProfilePopupElementId('profile_dropdown_pending', this.isPopup),
                class: 'component-dropdown-disabled',
                text: {key: 'strings:profilePage.friends.friendRequestSent'},
            },
        }

        void this.init()
    }

    private async init() {
        this.profileIsCurrentUser = this.isProfileCurrentUser()
        if(!this.profileIsCurrentUser) {
            this.hideFriendsTab()
            this.redirectIfFriendSectionIsActive()
        }

        this.userFriendsData = await UIUtilsFriends.getFriends(ClientUserState.userState)
        if(this.profileLinkController.friendsSectionLink != undefined) this.profileLinkController.friendsSectionLink.hasMadeAPICall = true
        await this.createDropdown()

        if(this.isPopup) return
        new UIProfileFriendsTableController(this.userFriendsData)
    }

    setUsername(username: string) {
        this.username = username
    }

    hideFriendsTab() {
        if(this.profileLinkController.friendsSectionLink == undefined) return
        this.profileLinkController.friendsSectionLink.style.display = 'none'
    }

    showFriendsTab() {
        if(this.profileLinkController.friendsSectionLink == undefined) return
        this.profileLinkController.friendsSectionLink.style.display = 'flex'
    }

    private redirectIfFriendSectionIsActive() {
        if(this.profileLinkController.friendsSectionLink == undefined) return
        if(this.profileLinkController.activeLink != this.profileLinkController.friendsSectionLink) return
        navigateToPage(`/profile/${this.username}`)
    }

    private isProfileCurrentUser(): boolean {
        return ClientUserState.userState?.username.toLowerCase() == decodeURIComponent(this.username.toLowerCase())
    }

    async createDropdown() {
        const friendDropdownContent = this.getInitialDropdownContentForUserFriendsData()
        this.profileDropdownContent = this.getDropdownContent(friendDropdownContent)
        if(this.toggleVisibilityComponent == undefined) return
        const dropdownParentId = getProfilePopupElementId('sticky_player_info_text_container', this.isPopup)
        this.dropdownController = new UIComponentDropdownController(this.profileDropdownContent, dropdownParentId, this.profileIsCurrentUser, this.toggleVisibilityComponent)
        this.dropdownController.dropdownView.style.left = '0'
    }

    private getDropdownContent(friendDropdownContent: DropdownContent): DropdownContent[] {
        const dropdownContent: DropdownContent[] = []
        if(!UIUtilsEjsVariables.isUserOnDiscordMobile) {
            dropdownContent.push({
                id: getProfilePopupElementId('profile_dropdown_gift_membership', this.isPopup),
                class: '',
                text: {key: 'strings:storePage.sendGift'},
                action: () => this.sendGiftDropdownAction(),
                icon: LobbyImage.StoreGiftMembershipDark,
            })
        }
        dropdownContent.push({
            id: getProfilePopupElementId('profile_dropdown_report', this.isPopup),
            class: 'component-dropdown-red',
            text: {key: 'strings:game.endGame.report'},
            action: () => void this.reportProfileDropdownAction(),
        })
        if(!ClientUserState.hasUser()) return dropdownContent

        this.isFriendOrPending ? dropdownContent.push(friendDropdownContent) : dropdownContent.unshift(friendDropdownContent)
        return dropdownContent
    }

    private getInitialDropdownContentForUserFriendsData(): DropdownContent {
        if(this.userFriendsData == undefined) return this.friendStateDropdownContent.notFriend
        if(this.userFriendsData.friends.some((friend) => friend.username == this.username)) {
            this.isFriendOrPending = true
            return this.friendStateDropdownContent.friend
        }
        if(this.userFriendsData.friendRequestsSent.includes(this.username)) {
            this.isFriendOrPending = true
            return this.friendStateDropdownContent.pending
        }
        return this.friendStateDropdownContent.notFriend
    }

    private sendGiftDropdownAction() {
        const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.PlayerProfile,
        }
        void CA.sendAnalyticEvent(giftButtonClickEventData)
        const url = ((UIUtilsEjsVariables.isUserOnDiscord && this.isPopup) ? 'https://colonist.io' : '') + `/store?username=${this.username}#gift`
        doActionIfCanGiftMembership(() => navigateToPage(url, true), this.username)
    }

    private async addFriendDropdownAction() {
        const updateDropdownOnSuccess = (becameFriends: boolean) => {
            const dropdownElement = document.getElementById(getProfilePopupElementId('profile_dropdown_add_friend', this.isPopup))
            if(dropdownElement == undefined) return
            dropdownElement.remove()

            const newDropdownContent = becameFriends ? this.friendStateDropdownContent.friend : this.friendStateDropdownContent.pending
            this.dropdownController.createDropdownContentElement(newDropdownContent)
        }
        void UIUtilsFriends.addFriendAction(decodeURIComponent(this.username), updateDropdownOnSuccess)
    }

    private showRemoveFriendPopup() {
        const removeFriendPopupAction = () => {
            const updateDropdownOnSuccess = () => {
                const dropdownElement = document.getElementById(getProfilePopupElementId('profile_dropdown_removeFriend', this.isPopup))
                if(dropdownElement == undefined) return
                dropdownElement.remove()

                this.dropdownController.createDropdownContentElement(this.friendStateDropdownContent.notFriend)
            }
            void UIUtilsFriends.removeFriendAction(this.username, updateDropdownOnSuccess)
        }
        UIUtilsFriends.showRemoveFriendPopup(this.username, removeFriendPopupAction)
    }

    private async reportProfileDropdownAction() {
        if(!ClientUserState.userState?.isLoggedIn) {
            new ViewPopupLogin()
            return
        }
        const reportPopupAction = (playerName: string, reportReason: UserReportReasonEnum) => {
            const userReportData: AjaxDataTypes.UserReportData = {reason: reportReason}
            void UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiReportPlayer(playerName), undefined, userReportData, 'application/json')
        }
        UIPopupReportPlayer.createReportPopup(this.username, reportPopupAction, {key: 'strings:popups.reportPopup.success'})
    }
}
